import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowRight, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/lib/seo";
import Reveal from "@/components/site/Reveal";
import { services } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_IMG =
  "https://images.unsplash.com/photo-1648220073452-648960ffec43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjB3YWxraW5nJTIwc3VubnklMjBzdHJlZXR8ZW58MHx8fHwxNzgyMzk4NzA2fDA&ixlib=rb-4.1.0&q=85";

const empty = {
  name: "",
  email: "",
  company: "",
  phone: "",
  preferred_date: "",
  service_interest: "",
  message: "",
};

export default function Contact() {
  useSeo({
    title: "Book a Consultation",
    description:
      "Book a free 30-minute consultation with The People Collective. Ontario-based HR consultancy for new businesses, small businesses, and newly unionized organizations.",
    path: "/contact",
  });

  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
      setForm(empty);
      toast.success("Thanks — we'll be in touch within one business day.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again or email us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#2B3329",
            color: "#FDFBF7",
            border: "1px solid #82917B",
            borderRadius: 0,
          },
        }}
      />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="tpc-eyebrow">Contact</p>
              <h1 className="mt-5 font-serif text-5xl sm:text-6xl leading-[1.05] tracking-tight">
                Book a <span className="italic text-[#82917B]">consultation.</span>
              </h1>
              <p className="mt-6 text-lg text-[#4A5348] leading-relaxed">
                A free, 30-minute conversation. No slides, no pressure. We’ll
                listen to what’s on your plate and tell you honestly whether
                we’re the right partner.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-5">
                <div className="flex items-start gap-4">
                  <span className="h-10 w-10 flex items-center justify-center bg-[#F3EFE7] text-[#82917B]">
                    <Mail size={18} />
                  </span>
                  <div>
                    <p className="tpc-eyebrow">Email</p>
                    <a
                      href="mailto:hello@thepeoplecollective.ca"
                      className="block mt-1 text-[#2B3329] hover:underline"
                      data-testid="contact-email"
                    >
                      hello@thepeoplecollective.ca
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="h-10 w-10 flex items-center justify-center bg-[#F3EFE7] text-[#82917B]">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="tpc-eyebrow">Where we work</p>
                    <p className="mt-1 text-[#2B3329]">Ontario, Canada</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="mt-12">
                <img
                  src={CONTACT_IMG}
                  alt="People walking on a sunny Ontario street"
                  className="w-full h-64 object-cover"
                />
              </div>
            </Reveal>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.06}>
              <div className="bg-[#F3EFE7] p-8 md:p-12 border border-[#82917B]/20">
                {done ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <CheckCircle2 size={48} className="text-[#82917B] mx-auto" />
                    <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight">
                      Thanks — your note is in.
                    </h2>
                    <p className="mt-4 text-[#4A5348] max-w-md mx-auto">
                      We typically respond within one business day. If it’s
                      urgent, email{" "}
                      <a
                        href="mailto:thepeoplecollective@outlook.com"
                        className="underline"
                      >
                        thepeoplecollective@outlook.com
                      </a>
                      .
                    </p>
                    <button
                      type="button"
                      onClick={() => setDone(false)}
                      data-testid="contact-send-another"
                      className="tpc-btn tpc-btn-secondary mt-8"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                    noValidate
                  >
                    <div className="md:col-span-2">
                      <p className="tpc-eyebrow">Book a Consultation</p>
                      <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">
                        Tell us a little about your team.
                      </h2>
                    </div>

                    <div>
                      <label className="tpc-eyebrow" htmlFor="name">
                        Name
                      </label>
                      <input
                        id="name"
                        required
                        data-testid="contact-name"
                        value={form.name}
                        onChange={update("name")}
                        className="tpc-input mt-2"
                        placeholder="Jordan Reeve"
                      />
                    </div>

                    <div>
                      <label className="tpc-eyebrow" htmlFor="email">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        required
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={update("email")}
                        className="tpc-input mt-2"
                        placeholder="jordan@yourcompany.ca"
                      />
                    </div>

                    <div>
                      <label className="tpc-eyebrow" htmlFor="company">
                        Company
                      </label>
                      <input
                        id="company"
                        data-testid="contact-company"
                        value={form.company}
                        onChange={update("company")}
                        className="tpc-input mt-2"
                        placeholder="Your organization"
                      />
                    </div>

                    <div>
                      <label className="tpc-eyebrow" htmlFor="phone">
                        Phone
                      </label>
                      <input
                        id="phone"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={update("phone")}
                        className="tpc-input mt-2"
                        placeholder="(555) 555-0100"
                      />
                    </div>

                    <div>
                      <label className="tpc-eyebrow" htmlFor="preferred_date">
                        Preferred date
                      </label>
                      <input
                        id="preferred_date"
                        type="date"
                        data-testid="contact-date"
                        value={form.preferred_date}
                        onChange={update("preferred_date")}
                        className="tpc-input mt-2"
                      />
                    </div>

                    <div>
                      <label className="tpc-eyebrow" htmlFor="service_interest">
                        Service of interest
                      </label>
                      <select
                        id="service_interest"
                        data-testid="contact-service"
                        value={form.service_interest}
                        onChange={update("service_interest")}
                        className="tpc-input mt-2 bg-white"
                      >
                        <option value="">Not sure yet</option>
                        {services.map((s) => (
                          <option key={s.slug} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="tpc-eyebrow" htmlFor="message">
                        What’s on your plate?
                      </label>
                      <textarea
                        id="message"
                        required
                        data-testid="contact-message"
                        value={form.message}
                        onChange={update("message")}
                        rows={5}
                        className="tpc-input mt-2 resize-y"
                        placeholder="Tell us a bit about your team, what's working, and where you'd like support."
                      />
                    </div>

                    <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                      <p className="text-xs text-[#5A6358]">
                        We respond within one business day.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit"
                        className="tpc-btn tpc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Sending..." : "Send request"}
                        {!submitting && <ArrowRight size={16} />}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
